<template>
  <div
    :class="[
      'inbox-remark-card',
      {
        'sf-commcard-user-can-check': todo && allowedActions.toggleDone,
        'sf-commcard-completed': done,
      },
    ]"
  >
    <div class="sf-pad-h-3 sf-pad-t-3 sf-full-width">
      <div class="sf-level sf-level-between">
        <div class="sf-level">
          <div class="sf-label sf-label-rectangular ledger-period-name">
            {{ ledgerPeriodName }}
          </div>

          <a
            v-if="itemUrl"
            class="sf-a sf-pointer sf-c-primary sf-font-medium"
            :href="contextUrl"
            @click="trackClick('context-name')"
          >
            {{ contextName }}
          </a>
          <div v-else class="sf-font-medium sf-c-text">
            {{ contextName }}
          </div>
        </div>
        <div v-if="itemUrl">
          <a
            class="sf-btn sf-level"
            :href="contextUrl"
            @click="trackClick('context-button')"
          >
            <RemarkBubble
              v-if="liquidInputFieldPath"
              :id="'remark-' + id"
              :linking-path="liquidInputFieldPath"
            />
            <span>{{ I18n.t('communication.cards.caption_btn.context') }}</span>
          </a>
        </div>
      </div>
    </div>
    <div class="sf-commcard-message">
      <div class="sf-commcard-message-content">
        <div
          v-if="todo"
          class="
            sf-commcard-message-content-toggle sf-form-check sf-form-check-todo
          "
        >
          <div class="sf-form-check-content">
            <div
              class="sf-form-check-indicator"
              @click.prevent="
                allowedActions.toggleDone ? toggleTodoDone(id) : null
              "
            />
          </div>
        </div>

        <div class="sf-commcard-message-content-post sf-commcard-post">
          <div class="sf-commcard-post-author">
            <div class="sf-commcard-post-name" :class="hideSessionClass">
              <i v-if="createdByBot" class="i-bot" />
              {{ authorName }}
            </div>

            <div
              v-if="editedAt"
              v-tooltip.timestamp="editedAt"
              class="sf-commcard-post-date"
            >
              {{ I18n.t('communication.cards.edited') }}
              {{ editedAtFormatted }}
            </div>

            <div
              v-else
              v-tooltip.timestamp="createdAt"
              class="sf-commcard-post-date"
            >
              {{ createdAtFormatted }}
            </div>
          </div>

          <editable-remark-tags-list
            v-if="showTags"
            :tag-ids="tagIds"
            :allowed-actions="allowedActions"
            :remark-id="id"
            :company-id="companyId"
            :class="hideSessionClass"
            @tags-updated="tagsUpdated"
          />

          <div
            v-dompurify-html="renderedText"
            class="sf-commcard-post-text"
            :class="hideSessionClass"
          />

          <div
            v-for="attachment in attachments"
            :key="attachment.id"
            class="sf-label sf-below-1 sf-level-item"
          >
            <div class="sf-level sf-level-small" :class="hideSessionClass">
              <a
                class="sf-a sf-level-item"
                :href="attachment.url"
                target="_blank"
                >{{ attachment.filename }}</a
              >
            </div>
          </div>

          <div
            v-if="doneAt && done && doneByName"
            v-tooltip.timestamp="doneAt"
            class="sf-font-xs sf-c-text-light"
            :class="hideSessionClass"
          >
            {{
              I18n.t('communication.cards.user_marked_as_done', {
                user: doneByName,
              })
            }}
          </div>

          <div class="sf-commcard-post-actions">
            <a
              v-if="allowedActions.edit"
              href="#"
              @click.prevent="openEditRemarkModal"
            >
              {{ I18n.t('common.edit') }}
            </a>
            <a
              v-if="allowedActions.remove"
              href="#"
              @click.prevent="handleRemoveClick"
            >
              {{ I18n.t('common.remove') }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="sf-commcard-comments sf-bg-white sf-pad-h-3">
      <comment-card
        v-for="comment in allComments"
        v-bind="comment"
        :key="comment.id"
        :commentable-id="id"
        commentable-type="Remark"
      />
    </div>

    <comment-form
      text=""
      btn-text="Reply"
      :follower-ids="followerIds"
      :commentable-id="id"
      commentable-type="Remark"
      :focus-form="false"
      :placeholder="I18n.t('communication.comments.enter_reply')"
    />
  </div>
</template>

<script>
import trackEvent from '@app/utils/track_event'

import RemarkCard from '@app/application/communication/sections/communication/components/remarks/remark-card'
import RemarkBubble from '@app/application/communication/sections/communication/components/remarks/remark-bubble'

import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'

export default {
  components: {
    RemarkBubble,
  },
  extends: RemarkCard,

  setup() {
    const hideSessionClass = useRedactSessionRecording()
    return {
      hideSessionClass,
    }
  },

  computed: {
    itemUrl() {
      const contextUrl = this.context.url

      if (contextUrl === '') {
        return null
      } else {
        const url = new URL(contextUrl, window.location.origin)
        if (!this.ledgerArchived) {
          // this.ledgerId is the original ledger the remark was on. if that is archived, don't redirect there
          url.searchParams.set('current_ledger_id', this.ledgerId)
        }
        return url.toString()
      }
    },

    contextUrl() {
      const context = this.context
      const wfReviewUrl = context.workflowReviewUrl

      if (wfReviewUrl) {
        return wfReviewUrl
      }
      const itemUrl = new URL(this.itemUrl)
      if (this.isCompanyContext || this.isDocumentContext) {
        itemUrl.hash = `remark-${this.id}`
      } else {
        itemUrl.searchParams.set('anchor', `remark-${this.id}`)
      }
      return itemUrl.toString()
    },

    contextName() {
      return this.context.name
    },

    context() {
      return this.contextByLedgerTypeAndId(
        this.ledgerId,
        this.contextType,
        this.contextId
      )
    },
  },

  methods: {
    trackClick(label) {
      trackEvent('inbox', 'click', label)
    },
  },
}
</script>

<style>
.inbox-remark-card .sf-commcard-reply-form-footer {
  background: white;
}

.inbox-remark-card .sf-commcard-message-content {
  padding-bottom: 0;
}

.inbox-remark-card .sf-commcard-post:last-child {
  border-bottom: 0;
}

.inbox-remark-card .sf-commcard-comments .sf-commcard-post-text {
  font-size: 13px;
}

.inbox-remark-card .sf-commcard-reply-form-files {
  background: none;
}

.inbox-remark-card .sf-commcard-comments .sf-commcard-post {
  padding-left: 0;
  padding-right: 0;
}

.ledger-period-name {
  white-space: nowrap;
}
</style>
